import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Grid,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  InputAdornment,
  Divider,
} from '@mui/material';
import { Close, Visibility, VisibilityOff } from '@mui/icons-material';
import adminService from '../../services/adminService';
import stateDistrictData from '../../libs/state&dist.json';

const GENDERS = ['Male', 'Female', 'Other'];
const PROFILE_FOR = ['Self', 'Son', 'Daughter', 'Brother', 'Sister', 'Relative', 'Friend'];
const LOOKING_FOR = ['Marriage', 'Companionship'];
const MEMBERSHIP_TYPES = ['Free', 'Premium'];
const COUNTRIES = ['India', 'USA', 'UK', 'Canada', 'Australia', 'UAE', 'Singapore', 'New Zealand', 'Germany', 'France', 'Italy', 'Malaysia', 'Saudi Arabia', 'Qatar', 'Other'];
const CITIZENSHIP = ['Citizen', 'Permanent Resident', 'Work Permit', 'Student Visa', 'Other'];
const INDIAN_STATES = stateDistrictData.states.map(({ state }) => state);
const DISTRICTS_BY_STATE = stateDistrictData.states.reduce((districtMap, { state, districts }) => {
  districtMap[state] = districts;
  return districtMap;
}, {});

const defaultForm = {
  name: '',
  email: '',
  phone: '',
  password: '',
  gender: '',
  profileFor: '',
  lookingFor: 'Marriage',
  isEmailVerified: 'true',
  isVerified: 'false',
  membershipType: 'Free',
  locationDetails: {
    country: 'India',
    state: '',
    city: '',
    citizenshipStatus: 'Citizen',
  },
};

const AddUserDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState(defaultForm);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  const setLocation = (patch) => setForm((prev) => ({
    ...prev,
    locationDetails: {
      ...prev.locationDetails,
      ...patch,
    },
  }));

  const handleClose = () => {
    setForm(defaultForm);
    setError('');
    setSaving(false);
    onClose();
  };

  const validate = () => {
    if (!form.name.trim()) return 'Name is required';
    if (!form.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Enter a valid email';
    if (!form.phone.trim()) return 'Phone is required';
    if (!/^\+?[0-9]{7,15}$/.test(form.phone.replace(/\s/g, ''))) return 'Enter a valid phone number';
    if (!form.password) return 'Password is required';
    if (form.password.length < 6) return 'Password must be at least 6 characters';
    if (!form.gender) return 'Gender is required';
    if (!form.profileFor) return 'Profile For is required';
    return '';
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setSaving(true);
    setError('');
    try {
      await adminService.createUser({
        ...form,
        isEmailVerified: form.isEmailVerified === 'true',
        isVerified: form.isVerified === 'true',
      });
      if (onCreated) onCreated();
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Add New User</Typography>
        <IconButton onClick={handleClose} size="small"><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          {/* Account credentials */}
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Account Credentials
            </Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField fullWidth required label="Full Name" value={form.name} onChange={set('name')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required label="Email" type="email" value={form.email} onChange={set('email')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth required label="Phone" value={form.phone} onChange={set('phone')}
              placeholder="+91XXXXXXXXXX" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth required label="Password" value={form.password} onChange={set('password')}
              type={showPassword ? 'text' : 'password'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword((v) => !v)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Personal Details */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Personal Details
            </Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField select fullWidth required label="Gender" value={form.gender} onChange={set('gender')}>
              {GENDERS.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth required label="Profile For" value={form.profileFor} onChange={set('profileFor')}>
              {PROFILE_FOR.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth required label="Looking For" value={form.lookingFor} onChange={set('lookingFor')}>
              {LOOKING_FOR.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </TextField>
          </Grid>

          {/* Admin Verification */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Verification &amp; Membership
            </Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField select fullWidth label="Email Verified" value={form.isEmailVerified} onChange={set('isEmailVerified')}>
              <MenuItem value="true">Verified</MenuItem>
              <MenuItem value="false">Not Verified</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth label="User Verified" value={form.isVerified} onChange={set('isVerified')}>
              <MenuItem value="true">Verified</MenuItem>
              <MenuItem value="false">Not Verified</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth label="Membership" value={form.membershipType} onChange={set('membershipType')}>
              {MEMBERSHIP_TYPES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
          </Grid>

          {/* Location Details */}
          <Grid item xs={12} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Location
            </Typography>
            <Divider sx={{ mt: 0.5, mb: 1 }} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Country"
              value={form.locationDetails.country}
              onChange={(e) => setLocation({ country: e.target.value, state: '', city: '' })}
            >
              {COUNTRIES.map((country) => <MenuItem key={country} value={country}>{country}</MenuItem>)}
            </TextField>
          </Grid>

          {form.locationDetails.country === 'India' && (
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="State"
                value={form.locationDetails.state}
                onChange={(e) => setLocation({ state: e.target.value, city: '' })}
              >
                <MenuItem value=""><em>Select State</em></MenuItem>
                {INDIAN_STATES.map((state) => <MenuItem key={state} value={state}>{state}</MenuItem>)}
              </TextField>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            {form.locationDetails.country === 'India' ? (
              <TextField
                select
                fullWidth
                label="District"
                value={form.locationDetails.city}
                onChange={(e) => setLocation({ city: e.target.value })}
                disabled={!form.locationDetails.state}
              >
                <MenuItem value=""><em>Select District</em></MenuItem>
                {(DISTRICTS_BY_STATE[form.locationDetails.state] || []).map((district) => (
                  <MenuItem key={district} value={district}>{district}</MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                fullWidth
                label="City"
                value={form.locationDetails.city}
                onChange={(e) => setLocation({ city: e.target.value })}
                placeholder="Type city"
              />
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Citizenship Status"
              value={form.locationDetails.citizenshipStatus}
              onChange={(e) => setLocation({ citizenshipStatus: e.target.value })}
            >
              {CITIZENSHIP.map((status) => <MenuItem key={status} value={status}>{status}</MenuItem>)}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {saving ? 'Creating...' : 'Create User'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserDialog;
